import "@progress/kendo-date-math/tz/America/Chicago";
import {
  Flexbox,
  FormField,
  Grid,
  Label,
  RangeDatePicker,
  RangePickerValueType,
  Button as SDSButton,
  Select,
  TextInput
} from "@sede-x/shell-ds-react-framework";
import {
  ArrowCcw,
  Search
} from "@sede-x/shell-ds-react-framework/build/esm/components/Icon/components";
import dayjs, { Dayjs } from "dayjs";
import { debounce } from "lodash";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import LoadingPanel from "shared/components/LoadingPanel";
import { usePicklists } from "ticketing/contexts/picklists/PicklistContextProvider";
import useSearchBatches from "ticketing/hooks/useSearchBatches";
import { TicketSource, TicketStatus, TTicketSearchCriteria } from "ticketing/ticketing.types";
import "./SearchTickets.css";

const labelStyle = {
  fontWeight: "bold",
  whiteSpace: "nowrap",
  textAlign: "right" as const
};
const MIN_WAIT_MILLISEC = 500;

type TSearchTicketsProps = {
  searchCriteria?: TTicketSearchCriteria;
  onSearch: (criteria: TTicketSearchCriteria) => void;
  onReset?: () => void;
  loading: boolean;
};
const MIN_FILTER_LENGTH = 3;

/**
 *
 * @param props
 * @returns
 */
export const SearchTicketsForm = (props: TSearchTicketsProps) => {
  const { picklists, isLoading, isReady } = usePicklists();
  const [deliveryIds, setDeliveryIds] = useState("");

  const onSubmit = (data: TTicketSearchCriteria) => {
    props.onSearch?.(data);
  };

  const [searchBatches, batchSearchResults, bLoading] = useSearchBatches();

  const debounceSearch = (searchFn: (e: string) => void) =>
    debounce((e: string) => {
      if (e.length >= MIN_FILTER_LENGTH) {
        searchFn(e.toString());
      }
    }, MIN_WAIT_MILLISEC);

  const [searchDateRange, setSearchDateRange] = useState<RangePickerValueType<Dayjs>>([
    props.searchCriteria?.startDate ? dayjs(props.searchCriteria.startDate) : null,
    props.searchCriteria?.endDate ? dayjs(props.searchCriteria.endDate) : null
  ]);

  const hasDeliveryIds = (deliveryIds.trim()?.length ?? 0) > 0;

  const { control, handleSubmit, reset } = useForm<TTicketSearchCriteria>({
    defaultValues: props.searchCriteria
  });

  const resetForm = () => {
    setSearchDateRange([
      props.searchCriteria?.startDate ? dayjs(props.searchCriteria.startDate) : null,
      props.searchCriteria?.endDate ? dayjs(props.searchCriteria.endDate) : null
    ]);
    reset();
    props.onReset?.();
  };

  if (isLoading || !isReady) {
    return <LoadingPanel />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid columns={"repeat(3, 6rem 1fr)"} columnGap="8px" rowGap="0px">
        <Grid.Cell centerContent={true}>
          <Label style={labelStyle}>Start/End Date</Label>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="startDate"
              render={({ field: startDateField }) => (
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field: endDateField }) => (
                    <RangeDatePicker
                      value={searchDateRange}
                      size="small"
                      picker="date"
                      placeholder={["Start date", "End date"]}
                      allowClear={false}
                      style={{ zIndex: "1" }}
                      onChange={dates => {
                        if (dates?.[0]) {
                          startDateField.onChange(dates[0].toDate());
                        } else {
                          startDateField.onChange(null);
                        }

                        if (dates?.[1]) {
                          endDateField.onChange(dates[1].toDate());
                        } else {
                          endDateField.onChange(null);
                        }

                        setSearchDateRange(dates);
                      }}
                      disabled={hasDeliveryIds}
                    />
                  )}
                />
              )}
            />
          </FormField>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <Label style={labelStyle}>Batch Name</Label>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="batches"
              render={({ field }) => (
                <Select
                  value={field.value?.map(v => v.id)}
                  onChange={(_, selOpts) => field.onChange(selOpts)}
                  onClear={() => field.onChange([])}
                  size="small"
                  fieldNames={{ label: "name", value: "id" }}
                  optionLabelProp="name"
                  optionFilterProp="name"
                  //@ts-ignore
                  options={batchSearchResults.length ? batchSearchResults : field.value}
                  loading={bLoading}
                  mode="multiple"
                  showSearch={true}
                  onSearch={e => debounceSearch(searchBatches)(e)}
                  disabled={hasDeliveryIds}
                />
              )}
            />
          </FormField>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <Label style={labelStyle}>MoT</Label>
        </Grid.Cell>
        <Grid.Cell>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="modeOfTransports"
              render={({ field }) => (
                <Select
                  value={field.value?.map(v => v.id)}
                  onChange={(_, selOpts) => field.onChange(selOpts)}
                  onClear={() => field.onChange([])}
                  size="small"
                  fieldNames={{ label: "name", value: "id" }}
                  optionLabelProp="name"
                  optionFilterProp="name"
                  //@ts-ignore
                  options={picklists?.modeOfTransports}
                  mode="multiple"
                  disabled={hasDeliveryIds}
                />
              )}
            />
          </FormField>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <Label style={labelStyle}>Source</Label>
        </Grid.Cell>
        <Grid.Cell>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="ticketSource"
              render={({ field }) => {
                // Transform field.value to match option structure, ignoring case
                const transformedValue = Array.isArray(field.value)
                  ? field.value
                      .map(val => {
                        if (typeof val === "string") {
                          // Find matching enum value (case-insensitive)
                          const matchingEntry = Object.entries(TicketSource).find(
                            ([key, enumVal]) =>
                              key.toLowerCase() === val.toLowerCase() ||
                              enumVal.toLowerCase() === val.toLowerCase()
                          );

                          return matchingEntry ? matchingEntry[1] : null;
                        }
                        return (val as { id?: string })?.id ?? val;
                      })
                      .filter(Boolean)
                  : [];

                return (
                  <Select
                    mode="multiple"
                    size="small"
                    value={transformedValue}
                    onChange={field.onChange}
                    onClear={() => field.onChange([])}
                    fieldNames={{ label: "name", value: "id" }}
                    optionLabelProp="name"
                    optionFilterProp="name"
                    options={Object.entries(TicketSource).map(([key, value]) => ({
                      id: value,
                      name: key,
                      label: key
                    }))}
                    disabled={hasDeliveryIds}
                  />
                );
              }}
            />
          </FormField>
        </Grid.Cell>

        <Grid.Cell centerContent={true}>
          <Label style={labelStyle}>Status</Label>
        </Grid.Cell>
        <Grid.Cell>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="ticketStatus"
              render={({ field }) => (
                <Select
                  size="small"
                  value={field.value ?? null}
                  onChange={value => {
                    field.onChange(value === undefined ? null : value);
                  }}
                  onClear={() => {
                    field.onChange(null);
                  }}
                  options={Object.values(TicketStatus)
                    .filter(v => v !== TicketStatus.Cancelled)
                    .map(status => ({ label: status, value: status }))}
                  onDeselect={() => {
                    field.onChange(null);
                  }}
                  disabled={hasDeliveryIds}
                  allowClear={true}
                />
              )}
            />
          </FormField>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <Label style={labelStyle}>Ticket#</Label>
        </Grid.Cell>
        <Grid.Cell>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="ticketNumbers"
              render={({ field }) => (
                <TextInput
                  size="small"
                  value={field.value ?? ""}
                  onChange={val => field.onChange(val)}
                  onClear={() => field.onChange("")}
                  placeholder="comma separated ticket numbers"
                  clearable
                />
              )}
            />
          </FormField>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <Label style={{ ...labelStyle, whiteSpace: "wrap" }}>Last Modified By</Label>
        </Grid.Cell>
        <Grid.Cell>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="lastModifiedBy"
              render={({ field }) => (
                <TextInput
                  size="small"
                  value={field.value ?? ""}
                  onChange={val => field.onChange(val)}
                  onClear={() => field.onChange("")}
                  placeholder="comma separated user ids"
                  clearable
                  disabled={hasDeliveryIds}
                />
              )}
            />
          </FormField>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>
          <Label style={labelStyle}>Delivery Ids</Label>
        </Grid.Cell>
        <Grid.Cell>
          <FormField size="small" style={{ marginBottom: "4px" }}>
            <Controller
              control={control}
              name="deliveryIds"
              render={({ field }) => (
                <TextInput
                  size="small"
                  value={field.value ?? ""}
                  onChange={event => {
                    // Update both the form field value AND your state
                    const newValue = event.target.value;
                    field.onChange(newValue);
                    setDeliveryIds(newValue);
                  }}
                  onClear={() => {
                    field.onChange("");
                    setDeliveryIds("");
                  }}
                  placeholder="comma separated delivery ids"
                  clearable
                />
              )}
            />
          </FormField>
        </Grid.Cell>
        <Grid.Cell centerContent={true}>&nbsp;</Grid.Cell>
        <Grid.Cell centerContent={true}>
          <FormField size="small" style={{ width: "100%", marginBottom: "4px" }}>
            <Flexbox justifyContent="center" alignItems="center" gap="0.5rem">
              <SDSButton
                icon={<Search />}
                disabled={props.loading}
                size="small"
                style={{ marginTop: "5px", flex: 1 }}
                type={"submit"}>
                Search
              </SDSButton>
              <SDSButton
                icon={<ArrowCcw />}
                size="small"
                style={{ marginTop: "5px", flex: 1 }}
                onClick={resetForm}>
                Reset
              </SDSButton>
            </Flexbox>
          </FormField>
        </Grid.Cell>
      </Grid>
    </form>
  );
};
